import type { ChartData } from 'chart.js'
import type { useProjectDataVisualizationsStore } from '~/stores/projectDataVisualizations/projectDataVisualizations'
import { useProjectStore } from '~/stores/projects'
import {
  type DataVizGetStatisticalValueSummaryDTO,
  type TransferObjectsSignificantResultsSummaryDTO,
  type QueriesGroups,
  type TransferObjectsDataVizAnalysisFilterDataDTO,
  type DataVizTop10BiggestGapsDTO,
  type DataVizOverviewPADTO,
  type QueriesWorkforceRepresentationCharts,
  type QueriesGetWorkforceRepresentationResponse,
} from 'client/sratool'
import type { ITilesData } from './charts'
import type { InjectionKey } from 'vue'
import type { useChartRenderTracker } from '~/composables/useChartRenderTracker'
import type { ExecutiveSummaryChartKey } from './executiveSummary'

export interface NoncompetitivePersonnelActivityType {
  ShortName: 'Promo' | 'Term'
  LongName: 'Promotions' | 'Terminations'
}

export interface SelectionRateAnalysisPersonnelActivityType {
  ShortName: 'Promo' | 'Term' | 'Hire' | 'Selection'
  LongName: 'Promotions' | 'Terminations' | 'Hires' | 'Selections'
}

export type PickByFullKeyPattern<T, Pattern extends string> = Pick<
  T,
  Extract<keyof T, `${Pattern}`>
>

export type PickByPrefix<T, Prefix extends string> = PickByFullKeyPattern<
  T,
  `${Prefix}${string}`
>

export interface FrequencyOfStatisticalImpactChart {
  id: number
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
    separatedChartData?: ChartData<'bar'> | undefined
  }
  insight?: string
  frequencyLabelOffset: number
  laborMarketType?: string
  executiveSummaryId: ExecutiveSummaryChartKey
}

export interface StandardDeviationSeverityChart {
  id: number
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
  insight?: string
  laborMarketType?: string
  executiveSummaryId: ExecutiveSummaryChartKey
}

export interface SignificantResultsSummary {
  hierarchyID: string
  data: TransferObjectsSignificantResultsSummaryDTO
  children: SignificantResultsSummary[]
  expanded: boolean
}

export interface SignificantResultsSummaryTable {
  groupField: QueriesGroups
  data: SignificantResultsSummary[]
}

export interface StatisticalValuesSummaryTable {
  groupField: QueriesGroups
  data: DataVizGetStatisticalValueSummaryDTO
}

export interface TopTenDispositionChart {
  id: number
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
}

export interface ExcludedApplicantsChart {
  id: number
  protectedClass: string
  totalNumber: number
  chart?: {
    data: ChartData<'doughnut'>
  }
}

export type NoncompetitivePromotionsTileTitle =
  | 'Total Promotions'
  | 'Total Terminations'
  | `Significant Results: ${string}`

export interface NoncompetitivePromotionsTiles extends ITilesData {
  title: NoncompetitivePromotionsTileTitle
}

export type WorkforceRepresentationData =
  QueriesWorkforceRepresentationCharts & {
    workforceRepresentationOverallTenure?: QueriesGetWorkforceRepresentationResponse['workforceRepresentationOverallTenure']
  }

export type WorkforceRepresentationListProperty =
  | 'Overview'
  | 'Tenure'
  | 'PayGrade'
  | 'PayDecile'
  | 'SecondLowest'
  | 'OverallTenure'

type WorkforceRepresentationTileTitle =
  | 'Total Employees'
  | 'Hiring Rate'
  | 'Overall Promotion Rate'
  | 'Turnover Rate'

export interface WorkforceRepresentationTiles extends ITilesData {
  title: WorkforceRepresentationTileTitle
}

export interface WorkforceRepresentationDoughnutChart {
  id: number
  protectedClass: string
  chart?: {
    data: ChartData<'doughnut'>
  }
  executiveSummaryId: ExecutiveSummaryChartKey
}

export interface WorkforceRepresentationStackedBarChart {
  id: number
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
  insight?: string
  executiveSummaryId: ExecutiveSummaryChartKey
}

export interface WorkforceRepresentationBarChart {
  id: number
  category: string
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
  insight?: string
  executiveSummaryId: ExecutiveSummaryChartKey
}

export interface PayCategoryChart {
  id: number
  category: string
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
  insight?: string
}

export interface LaborMarketChart {
  id: number
  laborMarketType: string
  dataType: string
  protectedClass: string
  chart?: {
    data: ChartData<'bar'>
  }
  insight?: string
}

export interface Top10BiggestGapsTable {
  laborMarketType:
    | 'Applicant Pool vs. Labor Market'
    | 'Workforce vs. Labor Market'
  dataType: 'Applicant Pool' | 'Workforce'
  groupField: QueriesGroups
  focalGroups: DropdownOption<string>[]
  data: DataVizTop10BiggestGapsDTO[]
}

export interface SummaryHashTable<
  T extends StatisticalValuesSummaryTable | SignificantResultsSummaryTable
> {
  [key: string]: T
}

export type LaborMarketHashTable<T extends Top10BiggestGapsTable> = {
  [key in `${T['dataType']}-${T['groupField']['groupFieldGUID']}`]: T
}

export interface DropdownOption<T> {
  value: T
  label: string
}

export interface MeritVariableData {
  difference: number
  average: string
}

export const EnumerationsSignificantResultsSummaryView = {
  Difference: 1,
  Averages: 2,
} as const

export type EnumerationsSignificantResultsSummaryView =
  (typeof EnumerationsSignificantResultsSummaryView)[keyof typeof EnumerationsSignificantResultsSummaryView]

export interface ProjectAnalysisOption
  extends TransferObjectsDataVizAnalysisFilterDataDTO {
  uniqueID: string
  label: string
}

export interface PersonnelActivityAnalysis extends DataVizOverviewPADTO {
  uniqueID: string
  showAnalysisName: boolean
}

export interface ExecutiveSummaryPersonnelActivityAnalysis
  extends PersonnelActivityAnalysis {
  executiveSummaryId: ExecutiveSummaryChartKey
}

export type StatisticalValuesSummaryTableSortColumn =
  | `StrataValue${1 | 2 | 3 | 4 | 5}`
  | `GroupFieldSignificance${number}`
  | null

export const ChartRenderTrackerKey: InjectionKey<
  ReturnType<typeof useChartRenderTracker>
> = Symbol('ChartRenderTracker')

export type AnalysisOption = ReturnType<
  typeof useProjectStore
>['analysesForSelectedProject'][number] & {
  label: string
}

export interface StrataOption {
  label: string
  strataFieldGUID: string
}

export interface AnalysisSelection {
  analysis: AnalysisOption | undefined
  strata: StrataOption | undefined
  selectionReady: boolean
  clearChartData?: (selectionKey: string | undefined) => void
}

export type ChartDependencyConfig = {
  analysisId: MaybeRef<string | undefined>
  strataId: MaybeRef<string | undefined>
  displayRef: Ref<boolean>
  enabled?: MaybeRef<boolean>
}

export type AnalysisLifecycleConfig = {
  analysisId: MaybeRef<string | undefined>
  strataId: MaybeRef<string | undefined>

  strataOptions: Ref<StrataOption[]>

  enabled?: MaybeRef<boolean>

  getStrataOptions: (analysisID: string) => Promise<StrataOption[]>

  getChartData: () => Promise<boolean>

  clearChartData?: (selectionKey: string | undefined) => void
}
